import { useRef, type ChangeEvent, type Dispatch, type FC, type RefObject, type SetStateAction } from 'react';
import type { Editor } from '@tiptap/react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Icons from '../../Elements/Icons/index.ts';
import { Tooltip } from '../../Feedback/Tooltip/index.tsx';
import { applyLink, getTestId } from './helpers.ts';
import { LinkTooltip } from './LinkTooltip.tsx';
import { PlaceholdersDropdown } from './PlaceholdersDropdown/index.tsx';
import { ToolbarItem, ToolbarItems, ToolbarWrapper } from './styled.ts';
import type { CustomToolbarItem } from './types.ts';

interface ToolbarProps {
  editor: Editor;
  disabled?: boolean;
  placeholders?: string[];
  additionalToolbarItems?: CustomToolbarItem[];
  isPreview: boolean;
  setIsPreview: Dispatch<SetStateAction<boolean>>;
  withPreviewToggle?: boolean;
  rawEditorValue: string;
  setRawEditorValue: Dispatch<SetStateAction<string>>;
  rawEditorRef: RefObject<HTMLTextAreaElement> | null;
  dataTestId?: string;
  allowImages?: boolean;
  onChange?: (value: string) => void;
}

export const Toolbar: FC<ToolbarProps> = ({
  editor,
  disabled,
  placeholders,
  additionalToolbarItems,
  isPreview,
  setIsPreview,
  withPreviewToggle,
  rawEditorValue,
  setRawEditorValue,
  rawEditorRef,
  dataTestId,
  allowImages,
  onChange,
}) => {
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const isSelectedTextEmpty = editor.state.selection.empty;

  const isBoldActive = editor.isActive('bold');
  const isItalicActive = editor.isActive('italic');
  const isUnderlineActive = editor.isActive('underline');
  const isStrikeActive = editor.isActive('strike');
  const isSubActive = editor.isActive('subscript');
  const isSuperActive = editor.isActive('superscript');
  const isOrderedListActive = editor.isActive('orderedList');
  const isBulletListActive = editor.isActive('bulletList');
  const isLinkActive = editor.isActive('link');

  const handleBoldClick = () => {
    editor.chain().focus().toggleBold().run();
  };
  const handleItalicClick = useCallbackRef(() => {
    editor.chain().focus().toggleItalic().run();
  });
  const handleUnderlineClick = useCallbackRef(() => {
    editor.chain().focus().toggleUnderline().run();
  });
  const handleStrikeClick = useCallbackRef(() => {
    editor.chain().focus().toggleStrike().run();
  });
  const handleSubClick = useCallbackRef(() => {
    editor.chain().focus().toggleSubscript().run();
  });
  const handleSuperClick = useCallbackRef(() => {
    editor.chain().focus().toggleSuperscript().run();
  });
  const handleOrderedListClick = useCallbackRef(() => {
    editor.chain().focus().toggleOrderedList().run();
  });
  const handleBulletListClick = useCallbackRef(() => {
    editor.chain().focus().toggleBulletList().run();
  });
  const handleLinkClick = useCallbackRef(() => {
    if (isSelectedTextEmpty) {
      return;
    }
    if (isLinkActive) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
  });
  const handleSaveLink = useCallbackRef((href: string) => {
    applyLink(editor, href);
  });
  const handleClearClick = useCallbackRef(() => {
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  });

  const handlePlaceholderClick = useCallbackRef((placeholder: string) => {
    if (isPreview) {
      editor.chain().focus().insertContent(`[${placeholder}]`).run();
    } else {
      if (!rawEditorRef?.current) {
        return;
      }
      const { selectionStart, selectionEnd } = rawEditorRef.current;
      setRawEditorValue((prev) => `${prev.substring(0, selectionStart)}[${placeholder}]${prev.substring(selectionEnd)}`);
    }
  });

  const handlePreviewClick = useCallbackRef(() => {
    if (isPreview) {
      setRawEditorValue(editor.getHTML());
      setIsPreview(false);
      setTimeout(() => rawEditorRef?.current?.focus(), 0);
    } else {
      editor.chain().focus().setContent(rawEditorValue, false).run();
      const normalized = editor.getHTML();
      if (normalized !== rawEditorValue) {
        setRawEditorValue(normalized);
        onChange?.(normalized);
      }
      setIsPreview(true);
    }
  });

  const handleImageClick = useCallbackRef(() => {
    imageInputRef.current?.click();
  });

  const handleImageChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      Array.from(files).forEach((file) => {
        const fileReader = new FileReader();

        fileReader.readAsDataURL(file);
        fileReader.onload = () => {
          editor
            .chain()
            .insertContentAt(editor.state.selection.anchor, {
              type: 'image',
              attrs: {
                src: fileReader.result,
              },
            })
            .focus()
            .run();
        };
      });
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  });

  return (
    <ToolbarWrapper data-testid={getTestId('toolbar', dataTestId)}>
      <ToolbarItems>
        {isPreview && (
          <>
            <ToolbarItem
              type='button'
              $active={isBoldActive}
              disabled={disabled}
              onClick={handleBoldClick}
              data-testid={getTestId('toolbar-bold', dataTestId)}>
              <Icons.Bold size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isItalicActive}
              disabled={disabled}
              onClick={handleItalicClick}
              data-testid={getTestId('toolbar-italic', dataTestId)}>
              <Icons.Italic size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isUnderlineActive}
              disabled={disabled}
              onClick={handleUnderlineClick}
              data-testid={getTestId('toolbar-underline', dataTestId)}>
              <Icons.Underline size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isStrikeActive}
              disabled={disabled}
              onClick={handleStrikeClick}
              data-testid={getTestId('toolbar-strike', dataTestId)}>
              <Icons.Strike size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isSubActive}
              disabled={disabled}
              onClick={handleSubClick}
              data-testid={getTestId('toolbar-subscript', dataTestId)}>
              <Icons.Subscript size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isSuperActive}
              disabled={disabled}
              onClick={handleSuperClick}
              data-testid={getTestId('toolbar-superscript', dataTestId)}>
              <Icons.Superscript size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isOrderedListActive}
              disabled={disabled}
              onClick={handleOrderedListClick}
              data-testid={getTestId('toolbar-ordered-list', dataTestId)}>
              <Icons.OrderedList size={20} />
            </ToolbarItem>
            <ToolbarItem
              type='button'
              $active={isBulletListActive}
              disabled={disabled}
              onClick={handleBulletListClick}
              data-testid={getTestId('toolbar-bullet-list', dataTestId)}>
              <Icons.BulletList size={20} />
            </ToolbarItem>
            {allowImages && (
              <ToolbarItem
                type='button'
                disabled={disabled}
                onClick={handleImageClick}
                data-testid={getTestId('toolbar-image', dataTestId)}>
                <Icons.Image size={20} />
                <input type='file' accept='image/*' onChange={handleImageChange} hidden ref={imageInputRef} />
              </ToolbarItem>
            )}
          </>
        )}
        {placeholders && (
          <PlaceholdersDropdown
            placeholders={placeholders}
            onPlaceholderClick={handlePlaceholderClick}
            disabled={disabled}
            dataTestId={dataTestId}
          />
        )}
        {isPreview && (
          <>
            <Tooltip
              type='popper'
              placement='bottom'
              trigger='click'
              arrow={false}
              disabled={isSelectedTextEmpty || isLinkActive}
              tooltip={<LinkTooltip editor={editor} saveLink={handleSaveLink} isCreating dataTestId={dataTestId} />}
              renderTooltipWithoutWrapper>
              <ToolbarItem
                type='button'
                $active={isLinkActive}
                disabled={disabled}
                onClick={handleLinkClick}
                data-testid={getTestId('toolbar-link', dataTestId)}>
                <Icons.Link size={20} />
              </ToolbarItem>
            </Tooltip>
            <ToolbarItem type='button' disabled={disabled} onClick={handleClearClick} data-testid={getTestId('toolbar-clear', dataTestId)}>
              <Icons.Clear size={20} />
            </ToolbarItem>
          </>
        )}
        {additionalToolbarItems?.map(({ Icon, onClick }, index) => (
          <ToolbarItem
            key={index}
            type='button'
            disabled={disabled}
            onClick={onClick}
            data-testid={getTestId(`toolbar-custom-${index}`, dataTestId)}>
            <Icon size={20} />
          </ToolbarItem>
        ))}
      </ToolbarItems>
      <ToolbarItems>
        {withPreviewToggle && (
          <ToolbarItem type='button' onClick={handlePreviewClick} data-testid={getTestId('toolbar-preview-toggle', dataTestId)}>
            <Icons.Preview size={20} filled={!isPreview} />
          </ToolbarItem>
        )}
      </ToolbarItems>
    </ToolbarWrapper>
  );
};
